import React, { useEffect, useState } from "react";
import apiClientAdmin from "../../../services/apiClientAdmin";
import CompanyForm from "../Company-Form/CompanyForm";
import CompanyTable from "../Company-Table/CompanyTable";
import ErrorDialog from "../ErrorDialog/ErrorDialog";
import Dialog from "../FormDialog/Dialog";
import LoadingStyle from "../../Loading/Loading";
import { getHeaders } from "../../ApiHeaders/apiHeaders";
import { datadogRum } from "@datadog/browser-rum";
import { SplitDivider, useResizableSplit } from "../../SplitPane/ResizableSplit";

interface ComapanySetupProps {
  registerTabSwitchGuard?: (guard: () => boolean | Promise<boolean>) => void;
  canDelete?: boolean;
}

const CompanySetup: React.FC<ComapanySetupProps> = ({
  registerTabSwitchGuard,
  canDelete = false,
}) => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState<{ title: string; details: string } | null>(
    null
  );
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [dialog, setDialog] = useState<{
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmButtonText: string;
    cancelButtonText?: string;
    onConfirm: () => void;
    onCancel?: () => void;
  }>({
    open: false,
    title: "",
    message: "",
    confirmButtonText: "",
    onConfirm: () => {},
  });

  const closeDialog = () => {
    setDialog({
      open: false,
      title: "",
      message: "",
      confirmButtonText: "",
      onConfirm: () => {},
    });
  };
  useEffect(() => {
    if (registerTabSwitchGuard) {
      registerTabSwitchGuard(() => {
        return new Promise<boolean>((resolve) => {
          if (isFormDirty || isCreateMode) {
            setDialog({
              open: true,
              title: "Unsaved Changes",
              message:
                "You have unsaved changes. Are you sure you want to discard them?",
              confirmButtonText: "Yes",
              cancelButtonText: "No",
              onConfirm: () => {
                setIsFormDirty(false);
                setIsCreateMode(false);
                closeDialog();
                resolve(true);
              },
              onCancel: () => {
                closeDialog();
                resolve(false);
              },
            });
          } else {
            resolve(true);
          }
        });
      });
    }
  }, [isFormDirty, isCreateMode]);
  // const handleApiError = (err: any, fallbackTitle: string) => {
  //   let title = fallbackTitle;
  //   let details = 'An unknown error occurred';

  //   if (err.response?.data?.error) {
  //     title = err.response.data.error.title || fallbackTitle;
  //     details = err.response.data.error.error.message || details;
  //   } else if (err.message) {
  //     if (err.message.includes('Network Error') || err.message.includes('timeout')) {
  //       title = 'Network Error';
  //       details = 'Unable to connect to the server. Check your internet connection.';
  //     } else {
  //       details = err.message;
  //     }
  //   }
  //   setError({ title, details });
  //   datadogRum.addError(err, { fallbackTitle });
  // };
  const handleApiError = (err: any, fallbackTitle: string) => {
    let title = fallbackTitle;
    let details = "An unknown error occurred";

    const statusCode = err.response?.status;
    const errorResponse = err.response?.data?.error;

    const backendMessage =
      errorResponse?.error?.message || errorResponse?.message;

    switch (statusCode) {
      case 400:
        title = "Bad Request";
        details = backendMessage || "The data you submitted is invalid.";
        break;
      case 401:
        title = "Acces Denied";
        details =
          "You're not authorized to access this content. Please log in or check your access permissions.";
        break;
      case 403:
        title = "Access Denied";
        details = "You need permission to access this site.";
        break;
      case 404:
        title = "Not Found";
        details =
          backendMessage || "The requested resource could not be found.";
        break;
      case 500:
        title = "Internal Server Error";
        details =
          backendMessage ||
          "Server error while fetching data. Please try again later.";
        break;
      default:
        if (
          err.message?.includes("Network Error") ||
          err.message?.includes("timeout")
        ) {
          title = "Network Error";
          details =
            "Unable to connect to the server. Check your internet connection.";
        } else {
          title = fallbackTitle;
          details =
            backendMessage || err.message || "An unexpected error occurred.";
        }
        break;
    }
    setError({ title, details });
    datadogRum.addError(err, { title, details });
  };

  // const fetchCompaniesWithAssets = async () => {
  //   const headers = await getHeaders();
  //   setLoading(true);

  //   try {
  //     console.log("Fetching companies...");
  //     const response = await apiClientAdmin.get('/company/', { headers });
  //     console.log("Company Response:", response.data);

  //     const companyList = response.data?.response?.data || [];

  //     if (!companyList.length) {
  //       console.warn("No companies found in API response");
  //     }

  // const updatedCompanies = await Promise.all(
  //   companyList.map(async (company: any) => {
  //     try {
  //       console.log(`Fetching details for company ID: ${company.company_id}`);
  //       const companyDetailsResponse = await apiClientAdmin.get(`/company/${company.company_id}/setup`, { headers });
  //       console.log(`Details for ${company.company_id}:`, companyDetailsResponse.data);

  //       const companyDataArray = companyDetailsResponse.data?.response?.data || [];
  //       const companyData = companyDataArray.flat(1)[0] || {};

  //       return {
  //         ...company,
  //         company_name: companyData.company_name || company.company_name,
  //         owner_name: companyData.owner_name || "N/A",
  //         domain_names: companyData.domain_names || [],
  //         assets: companyData.assets || [],
  //       };
  //     } catch (err) {
  //       console.error(`Failed to fetch details for ${company.company_id}`, err);
  //       return { ...company, assets: [] };
  //     }
  //   })
  // );

  //     console.log("Final Updated Companies:", updatedCompanies);
  //     setCompanies(updatedCompanies);
  //   } catch (err) {
  //     console.error('Failed to fetch companies:', err);
  //   } finally {
  //     setLoading(false);
  //   }
  // };
  const fetchCompaniesWithAssets = async () => {
    const headers = await getHeaders();
    setLoading(true);

    try {
      const response = await apiClientAdmin.get("/company/all/setup", { headers });
      let companyDataList = response.data?.response?.data || [];
      if (Array.isArray(companyDataList[0])) {
        companyDataList = companyDataList[0];
      }

      if (companyDataList.length === 0) {
        console.warn("No companies found in the response.");
      }

      const updatedCompanies = companyDataList.map((companyData: any) => {
        const enrichedAssets = (companyData.assets || []).map((asset: any) => ({
          ...asset,
          asset_name: asset.asset_name || "Unknown",
        }));

        return {
          ...companyData,
          company_name: companyData.company_name || "Unknown",
          owner_name: companyData.owner_name || "N/A",
          domain_names: companyData.domain_names || [],
          assets: enrichedAssets,
        };
      });

      setCompanies(updatedCompanies);
    } catch (err) {
      console.error("Failed to fetch company setup data:", err);
      handleApiError(err, "Failed to fetch company");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompaniesWithAssets();
  }, []);

  const handleCreateCompany = async (newCompany: any) => {
    const headers = await getHeaders();

    const isBasicCompanyOnly =
      (!newCompany.assets || newCompany.assets.length === 0) &&
      (!newCompany.owner_name || newCompany.owner_name.trim() === "") &&
      (!newCompany.domain_names || newCompany.domain_names.length === 0);

    try {
      if (isBasicCompanyOnly) {
        const payload = {
          company_name: newCompany.company_name,
          sys_created_by: newCompany.sys_created_by,
          sys_modified_by: newCompany.sys_modified_by,
        };

        const response = await apiClientAdmin.post("/company/", payload, {
          headers,
        });

        if (response.status === 200) {
          setDialog({
            open: true,
            title: "Success",
            message: `Company "${newCompany.company_name}" successfully created.`,
            confirmButtonText: "Close",
            onConfirm: closeDialog,
          });
          datadogRum.addAction("create_commpany", {
            companyName: newCompany.company_name,
          });
          fetchCompaniesWithAssets();
        }
      } else {
        const response = await apiClientAdmin.post("/company/setup", newCompany, {
          headers,
        });

        if (response.status === 200) {
          setDialog({
            open: true,
            title: "Success",
            message: `Company "${newCompany.company_name}" successfully created.`,
            confirmButtonText: "Close",
            onConfirm: closeDialog,
          });
          datadogRum.addAction("create_company", {
            companyName: newCompany.company_name,
          });
          fetchCompaniesWithAssets();
        } else {
          console.error("Unexpected response status:", response.status);
        }
      }
    } catch (err) {
      handleApiError(err, "Failed to Create Company");
    }
  };

  const handleUpdateCompany = async (updatedCompany: any) => {
    const headers = await getHeaders();
    const originalCompany = companies.find(
      (c) => c.company_id === updatedCompany.company_id
    );
    const companyNameChanged =
      originalCompany?.company_name !== updatedCompany.company_name;

    try {
      // If company_name has changed, call the separate API
      if (companyNameChanged) {
        const companyNamePayload = {
          company_code: updatedCompany.company_code,
          company_name: updatedCompany.company_name,
          sys_created_by: updatedCompany.sys_created_by,
          sys_modified_by: updatedCompany.sys_modified_by,
        };

        await apiClientAdmin.put(`/company/`, companyNamePayload, {
          headers,
          params: {
            company_id: updatedCompany.company_id,
          },
        });
      }

      // Proceed with the existing update for other fields
      const response = await apiClientAdmin.post(
        `/company/setup/${updatedCompany.company_id}`,
        updatedCompany,
        { headers }
      );

      if (response.status === 200) {
        fetchCompaniesWithAssets();
        setSelectedCompany(null);
        setDialog({
          open: true,
          title: "Success",
          message: `Company "${updatedCompany.company_name}" successfully updated.`,
          confirmButtonText: "Close",
          onConfirm: () => {
            closeDialog();
            setIsEditMode(false);
          },
        });
        datadogRum.addAction("update_company", {
          companyName: updatedCompany.company_name,
        });
      } else {
        console.error("Unexpected response status:", response.status);
      }
    } catch (err) {
      handleApiError(err, "Failed to Update Company");
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    const headers = await getHeaders();
  
    try {
      const response = await apiClientAdmin.delete(`/company/${companyId}`, {
        headers,
      });
  
      if (response.status === 200 || response.status === 204) {
        fetchCompaniesWithAssets();
        setSelectedCompany(null);
        setSelectedRow(null);
      }
  
      setDialog({
        open: true,
        title: "Success",
        message: "Company successfully deleted.",
        confirmButtonText: "Close",
        onConfirm: closeDialog,
      });
  
      datadogRum.addAction("delete_company", {
        companyId,
      });
  
    } catch (err) {
      handleApiError(err, "Failed to Delete Company");
    }
  };

  // const handleEditCompany = (company: any) => {
  //   if (isFormDirty || isCreateMode) {
  //     setDialog({
  //       open: true,
  //       title: "Unsaved Changes",
  //       message: "You have unsaved changes. Are you sure you want to discard them?",
  //       confirmButtonText: "Yes",
  //       cancelButtonText: "No",
  //       onConfirm: () => {
  //         setIsFormDirty(false);
  //         setIsCreateMode(false);
  //         setSelectedCompany(company);
  //         setSelectedRow(company.company_name);
  //         closeDialog();
  //       },
  //       onCancel: closeDialog,
  //     });
  //   } else {
  //     setSelectedCompany(company);
  //         // setSelectedRow(company.company_name);
  //   }
  // };

  // const handleDeleteCompany = async (company_code: string) => {
  //   try {
  //     await apiClientAdmin.delete(`/company/${company_code}`);
  //     setCompanies(companies.filter((company) => company.company_code !== company_code));
  //     setSelectedCompany(null);
  //   } catch (err) {
  //     handleApiError(err, 'Failed to Delete Company');
  //   }
  // };

  const handleRowSelect = (company: any) => {
    if (isFormDirty || isCreateMode) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setIsCreateMode(false);
          setSelectedCompany(company);
          setSelectedRow(company.company_name);
          closeDialog();
          // setIsEditMode(false);
        },
        onCancel: closeDialog,
      });
    } else {
      // setIsEditMode(false);

      setSelectedCompany(company);
      setSelectedRow(company.company_name);
    }
  };
  // const handleClearSelection = () => {
  //   setSelectedCompany(null);
  // };
  const handleClearSelection = () => {
    if (isFormDirty) {
      setDialog({
        open: true,
        title: "Unsaved Changes",
        message:
          "You have unsaved changes. Are you sure you want to discard them?",
        // confirmButtonText: "Discard Changes",
        confirmButtonText: "Yes",
        cancelButtonText: "No",
        onConfirm: () => {
          setIsFormDirty(false);
          setSelectedCompany(null);
          setSelectedRow(null);
          closeDialog();
        },
        onCancel: closeDialog,
      });
    } else {
      setSelectedRow(null);
      setSelectedCompany(null);
    }
  };

  const split = useResizableSplit("xl");
  return (
    <div ref={split.containerRef} style={split.containerStyle} className={`flex flex-col xl:flex-row xl:items-stretch bg-gray-100 p-4 min-h-full gap-4 ${split.containerClassName}`}>
  
  {/* <div className="flex-grow lg:basis-1/2 flex flex-col relative"> */}
  <div className={`admin-tab-panel flex-grow flex flex-col lg:basis-1/3 xl:self-stretch bg-white relative ${split.leftClassName}`}>
  {loading && (
    <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-30">
      <LoadingStyle />
    </div>
  )}
    <CompanyTable
      companies={companies}
      // onDeleteCompany={handleDeleteCompany}
      // onEditCompany={handleEditCompany}
      onSearchChange={setSearchQuery}
      onClearSelection={handleClearSelection}
      selectedCompany={selectedCompany}
      // selectedRow={selectedRow} 
      onRowSelect={handleRowSelect}
      selectedRow={selectedRow} 
    />
  </div>

  {/* Company Form - Full width on small/medium, 1/3 width on large */}
  {/* <div  className="flex-grow lg:basis-1/3 flex flex-col h-full"> */}
  {/* <div className="flex-grow lg:basis-1/3 flex items-stretch"> */}
  {/* <div className="flex-grow flex flex-col lg:basis-1/4 xl:self-stretch "> */}
  <SplitDivider {...split.dividerProps} />
  <div className="admin-tab-panel admin-tab-form-panel flex-grow flex flex-col lg:basis-1/4 xl:self-stretch bg-white rounded-xl shadow-md">

    <CompanyForm
      onCreateCompany={handleCreateCompany}
      onUpdateCompany={handleUpdateCompany}
      selectedCompany={selectedCompany}
      onClearSelection={handleClearSelection}
      onDirtyChange={setIsFormDirty}
      onModeChange={(mode) => setIsCreateMode(mode === 'create')}
      onDeleteCompany={handleDeleteCompany}
      canDelete={canDelete}
    />
  </div>

      {dialog.open && (
        <Dialog
          title={dialog.title}
          message={dialog.message}
          confirmButtonText={dialog.confirmButtonText}
          cancelButtonText={dialog.cancelButtonText}
          onCancel={dialog.onCancel}
          onConfirm={dialog.onConfirm}
          onClose={closeDialog}
        />
      )}
      {error && (
        <ErrorDialog
          title={error.title}
          details={error.details}
          onClose={() => setError(null)}
        />
      )}
    </div>
  );
};

export default CompanySetup;
